import { Component, computed, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { Chip } from 'primeng/chip';
import { PageComponent } from '../../../infrastructure/shared/components/page/page';
import {
  ButtonComponent,
  ButtonSeverity,
  ButtonSize,
} from '../../../infrastructure/shared/ui/button/button';
import {
  IndividualPackageDto,
  PackageForIndividualResponse,
  PackagesClient,
  UserPackageStatus,
} from '../../../infrastructure/shared/model/fss.api';
import { AlertService } from '../../../infrastructure/shared/services/alert.service';
import { LoaderService } from '../../../infrastructure/shared/components/loader/loader.service';
import { UserService } from '../../../infrastructure/shared/services/user.service';
import { LocationIndividualService } from '../../../infrastructure/shared/services/location-individual.service';
import { ClientPackageStateService } from '../../../infrastructure/shared/services/client-package-state.service';
import { Cube01 } from '../../../infrastructure/shared/icons/cube-01/cube-01';
import { Clock } from '../../../infrastructure/shared/icons/clock/clock';
import { CheckCircle } from '../../../infrastructure/shared/icons/check-circle/check-circle';
import { InfoCircle } from '../../../infrastructure/shared/icons/info-circle/info-circle';
import { ChevronRight } from '../../../infrastructure/shared/icons/chevron-right/chevron-right';
import { CreditCard02 } from '../../../infrastructure/shared/icons/credit-card-02/credit-card-02';
import { Stars02 } from '../../../infrastructure/shared/icons/stars-02/stars-02';
import { Send01 } from '../../../infrastructure/shared/icons/send-01/send-01';
import { IndividualBuyPackageDialog } from './individual-buy-package-dialog/individual-buy-package-dialog';
import { getIndividualPackageName, isLastMinuteOnlyPackage } from './individual-package-naming';
import {
  PackageCard,
  PackageCardSection,
  getPackageSavings,
  isPopularPackage,
} from '../../../infrastructure/shared/components/package-card/package-card';
import {
  getDateFormatedSrFullMonth,
  getDateFormatedSrFullMonthWithTime,
  getDateFormatedSr,
} from '../../../infrastructure/shared/services/date.service';
import { File06 } from '../../../infrastructure/shared/icons/file-06/file-06';

@Component({
  standalone: true,
  selector: 'app-individual-packages-page',
  imports: [
    PageComponent,
    ButtonComponent,
    Chip,
    Cube01,
    Clock,
    CheckCircle,
    InfoCircle,
    ChevronRight,
    CreditCard02,
    Stars02,
    Send01,
    IndividualBuyPackageDialog,
    PackageCard,
    File06,
  ],
  templateUrl: './individual-packages-page.html',
  styleUrl: './individual-packages-page.scss',
})
export class IndividualPackagesPage {
  private _client = inject(PackagesClient);
  private _alert = inject(AlertService);
  private _loader = inject(LoaderService);
  private _location = inject(Location);
  private _user = inject(UserService);
  private _locationIndividual = inject(LocationIndividualService);
  private _packageState = inject(ClientPackageStateService);

  packages = signal<IndividualPackageDto[]>([]);
  currentPackage = signal<PackageForIndividualResponse | null>(null);
  loading = signal(true);
  buyingPackageId = signal<string | null>(null);
  dialogVisible = signal(false);
  selectedPkg = signal<IndividualPackageDto | null>(null);
  isHowItWorksExpanded = signal(true);

  hasActivePackage = computed(() => this.currentPackage() !== null);

  cards = computed(() => {
    const packages = this.packages();
    return packages.map((pkg, i) => ({
      pkg,
      name: getIndividualPackageName(pkg.hours ?? 0),
      savings: getPackageSavings(pkg, packages),
      isPopular: isPopularPackage(i, packages.length),
      isLastMinuteOnly: isLastMinuteOnlyPackage(pkg.hours ?? 0),
      sections: this._getSections(pkg),
    }));
  });

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;
  protected readonly UserPackageStatus = UserPackageStatus;
  protected readonly getIndividualPackageName = getIndividualPackageName;
  protected readonly getDateFormatedSrWithTime = getDateFormatedSrFullMonthWithTime;
  protected readonly getDateFormatedSr = getDateFormatedSr;
  protected readonly getDateFormatedSrFullMonth = getDateFormatedSrFullMonth;

  constructor() {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    Promise.all([this._loadPackages(), this._loadCurrentPackage()]).then(() => {
      this.loading.set(false);
    });
  }

  openBuyDialog(pkg: IndividualPackageDto): void {
    if (this.hasActivePackage()) return;
    this.selectedPkg.set(pkg);
    this.dialogVisible.set(true);
  }

  private _getSections(pkg: IndividualPackageDto): PackageCardSection[] {
    const cutoff = pkg.cancellationCutoff;
    // Express is booked only as a Last-Minute slot (next 24h, shown as the card's badge), so it is
    // always inside the cancellation cutoff: no free-cancellation line, only the charge rule.
    const lastMinuteOnly = isLastMinuteOnlyPackage(pkg.hours ?? 0);
    return [
      {
        title: 'Detalji paketa:',
        items: [`Do ${pkg.numOfUsers} učesnika`, `Važi ${pkg.daysValid} dana od dana aktivacije`],
      },
      {
        title: 'Politika otkazivanja:',
        items: lastMinuteOnly ? [] : [`Besplatno otkazivanje najkasnije ${cutoff}h pre termina.`],
        rules: [
          (pkg.cancellationPenalty ?? 0) >= 100
            ? `Ispod ${cutoff}h termin se naplaćuje u celosti.`
            : `Ispod ${cutoff}h naplaćuje se ${pkg.cancellationPenalty}% cene termina.`,
        ],
      },
    ];
  }

  buy(pkg: IndividualPackageDto): void {
    if (!pkg.id || this.buyingPackageId()) return;
    this.buyingPackageId.set(pkg.id);

    this._loader.setLoadingText = 'Slanje zahteva...';
    this._loader.setShowLoader = true;

    this._buyPackage(pkg.id)
      .then((res) => {
        this._alert.addSuccessMsg(res.result);
        this._packageState.load();
        this.load();
      })
      .finally(() => {
        this.buyingPackageId.set(null);
      });
  }

  goBack(): void {
    this._location.back();
  }

  goToCreateAppointment(): void {
    this._locationIndividual.routeToCreateAppointment();
  }

  getStatusChipLabel(status: UserPackageStatus | undefined): string {
    switch (status) {
      case UserPackageStatus.NEW:
        return 'Poslat zahtev';
      case UserPackageStatus.INVOICE_SENT:
        return 'Čeka se uplata';
      case UserPackageStatus.ACTIVE:
        return 'Uplata potvrđena';
      case UserPackageStatus.EXTENDED:
        return 'Produžen';
      case UserPackageStatus.EXPIRED:
        return 'Istekao';
      case UserPackageStatus.CANCELLED:
        return 'Otkazan';
      default:
        return '';
    }
  }

  getStatusChipClass(status: UserPackageStatus | undefined): string {
    switch (status) {
      case UserPackageStatus.NEW:
        return 'info sm';
      case UserPackageStatus.INVOICE_SENT:
        return 'warn sm';
      case UserPackageStatus.ACTIVE:
      case UserPackageStatus.EXTENDED:
        return 'success sm';
      case UserPackageStatus.CANCELLED:
        return 'red sm';
      case UserPackageStatus.EXPIRED:
        return 'gray sm';
      default:
        return 'gray sm';
    }
  }

  getBannerTitle(status: UserPackageStatus | undefined): string {
    switch (status) {
      case UserPackageStatus.NEW:
        return 'Zahtev je primljen';
      case UserPackageStatus.INVOICE_SENT:
        return 'Faktura je spremna';
      case UserPackageStatus.ACTIVE:
        return 'Paket je aktiviran';
      default:
        return '';
    }
  }

  getBannerSubtitle(status: UserPackageStatus | undefined): string {
    switch (status) {
      case UserPackageStatus.NEW:
        return 'Vaš zahtev je primljen i trenutno je u obradi.';
      case UserPackageStatus.INVOICE_SENT:
        return 'Faktura je poslata na vašu email adresu i čeka se uplata.';
      case UserPackageStatus.ACTIVE:
        return 'Vaša uplata je potvrđena i termini vam je sada dostupan.';
      default:
        return '';
    }
  }

  private async _loadPackages(): Promise<void> {
    const res = await firstValueFrom(this._client.getIndividualPackages());
    this.packages.set(res.result.packages ?? []);
  }

  private async _loadCurrentPackage(): Promise<void> {
    const userId = this._user.getUserData.id;
    const res = await firstValueFrom(this._client.getPackagesForIndividual(userId));
    const pkg = res.result;
    this.currentPackage.set(pkg && pkg.id ? pkg : null);
  }

  private async _buyPackage(id: string) {
    return await firstValueFrom(this._client.buyPackage(id));
  }
}
